export interface PermissionSnapshot {
  menu_permissions: string[];
  data_scope?: string;
  is_admin?: boolean;
}

export const NO_PERMISSION_PATH = '/no-permission';

// 菜单 ID 到权限键的映射
export const MENU_PERMISSION_MAP: Record<string, string> = {
  dashboard: 'dashboard',
  workbench: 'workbench',
  'internal-referral': 'internal_referral',
  'recruitment-pipeline': 'workflow',
  'job-profile': 'job_profile',
  'resume-management': 'resume_management',
  'intelligent-matching': 'intelligent_matching',
  'interview-schedule': 'interview_management',
  'offer-management': 'offer_management',
  'intelligent-orchestration': 'workflow',
  'platform-config': 'platform_config',
  'platform-basic-config': 'basic_info',
  'system-settings': 'system_settings',
  'operation-log': 'operation_log',
};

const ROUTE_PERMISSION_RULES: Array<{
  pathPrefix: string;
  permissionKey: string;
}> = [
  { pathPrefix: '/dashboard', permissionKey: 'dashboard' },
  { pathPrefix: '/workbench', permissionKey: 'workbench' },
  { pathPrefix: '/internal-referral', permissionKey: 'internal_referral' },
  { pathPrefix: '/recruitment-pipeline', permissionKey: 'workflow' },
  { pathPrefix: '/job-profile', permissionKey: 'job_profile' },
  { pathPrefix: '/resume-management', permissionKey: 'resume_management' },
  {
    pathPrefix: '/intelligent-matching',
    permissionKey: 'intelligent_matching',
  },
  { pathPrefix: '/matching', permissionKey: 'intelligent_matching' },
  {
    pathPrefix: '/interview-management',
    permissionKey: 'interview_management',
  },
  { pathPrefix: '/offer-management', permissionKey: 'offer_management' },
  { pathPrefix: '/platform-config', permissionKey: 'platform_config' },
  { pathPrefix: '/platform-basic-config', permissionKey: 'basic_info' },
  { pathPrefix: '/system-settings', permissionKey: 'system_settings' },
  { pathPrefix: '/user-management', permissionKey: 'system_settings' },
  { pathPrefix: '/operation-log', permissionKey: 'operation_log' },
  { pathPrefix: '/permission-management', permissionKey: 'system_settings' },
];

const POST_LOGIN_BLOCKED_PATHS = new Set([
  '/login',
  '/register',
  '/oauth/callback',
  '/admin/login',
]);

const POST_LOGIN_BLOCKED_PREFIXES = ['/admin'];

const FIRST_ACCESSIBLE_PATH_ORDER = [
  '/dashboard',
  '/workbench',
  '/internal-referral',
  '/recruitment-pipeline',
  '/job-profile',
  '/resume-management',
  '/intelligent-matching',
  '/interview-management',
  '/offer-management',
  '/platform-basic-config',
  '/platform-config',
  '/system-settings',
  '/operation-log',
];

const ALWAYS_ALLOWED_PATHS = new Set([NO_PERMISSION_PATH]);

function hasMenuPermission(
  permissions: PermissionSnapshot | null | undefined,
  permissionKey: string
): boolean {
  if (!permissions) {
    return false;
  }

  if (permissions.is_admin) {
    return true;
  }

  return permissions.menu_permissions.includes(permissionKey);
}

function getNormalizedMenuPermissions(
  permissions: PermissionSnapshot | null | undefined
): string[] {
  if (!permissions) {
    return [];
  }

  return Array.from(
    new Set(
      permissions.menu_permissions
        .map((item) => item.trim())
        .filter((item) => item.length > 0)
    )
  ).sort();
}

function normalizePathname(pathname: string): string {
  const collapsed = pathname.replace(/\/{2,}/g, '/');
  if (collapsed === '') {
    return '/';
  }
  if (collapsed !== '/' && collapsed.endsWith('/')) {
    return collapsed.slice(0, -1);
  }
  return collapsed;
}

function splitPath(path: string): { pathname: string; suffix: string } {
  const hashIndex = path.indexOf('#');
  const withoutHash = hashIndex >= 0 ? path.slice(0, hashIndex) : path;
  const hash = hashIndex >= 0 ? path.slice(hashIndex) : '';
  const searchIndex = withoutHash.indexOf('?');
  const pathname =
    searchIndex >= 0 ? withoutHash.slice(0, searchIndex) : withoutHash;
  const search = searchIndex >= 0 ? withoutHash.slice(searchIndex) : '';
  return { pathname, suffix: `${search}${hash}` };
}

function normalizePath(path: string): string {
  const withLeadingSlash = path.startsWith('/') ? path : `/${path}`;
  const { pathname, suffix } = splitPath(withLeadingSlash);
  return `${normalizePathname(pathname)}${suffix}`;
}

function pathMatchesPrefix(pathname: string, pathPrefix: string): boolean {
  return pathname === pathPrefix || pathname.startsWith(`${pathPrefix}/`);
}

function isAbsoluteLike(rawPath: string): boolean {
  return /^[a-zA-Z][a-zA-Z\d+.-]*:/.test(rawPath) || rawPath.startsWith('//');
}

function getCurrentOrigin(): string | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }
  return window.location.origin;
}

export function extractInternalPath(
  rawPath?: string | null,
  currentOrigin: string | undefined = getCurrentOrigin()
): string | null {
  if (!rawPath) {
    return null;
  }

  const trimmed = rawPath.trim();
  if (!trimmed) {
    return null;
  }

  if (!isAbsoluteLike(trimmed)) {
    return normalizePath(trimmed);
  }

  try {
    const parsed = new URL(trimmed, 'https://invalid.local');
    if (currentOrigin && parsed.origin !== currentOrigin) {
      return null;
    }
    return normalizePath(`${parsed.pathname}${parsed.search}${parsed.hash}`);
  } catch {
    return null;
  }
}

function getPathname(path: string): string {
  return getPathInfo(path).pathname;
}

function getPathInfo(path: string): {
  pathname: string;
  searchParams: URLSearchParams;
} {
  const internalPath = extractInternalPath(path) || '/';
  const { pathname, suffix } = splitPath(internalPath);
  const search = suffix.split('#')[0];
  const rawSearch = search.startsWith('?') ? search.slice(1) : search;
  return {
    pathname: normalizePathname(pathname),
    searchParams: new URLSearchParams(rawSearch),
  };
}

function isOfferDetailPath(pathname: string): boolean {
  if (!pathMatchesPrefix(pathname, '/offer-management')) {
    return false;
  }
  if (pathname === '/offer-management') {
    return false;
  }
  return !pathname.endsWith('/edit');
}

function isInterviewDetailPath(pathname: string): boolean {
  if (!pathMatchesPrefix(pathname, '/interview-management')) {
    return false;
  }
  return pathname !== '/interview-management';
}

function isBusinessScreeningDeepLink(
  pathname: string,
  searchParams: URLSearchParams
): boolean {
  if (pathname !== '/recruitment-pipeline') {
    return false;
  }

  const candidateId = (searchParams.get('candidateId') || '').trim();
  const normalizedType = (searchParams.get('type') || '')
    .trim()
    .toLowerCase()
    .replace(/-/g, '_');

  return candidateId.length > 0 && normalizedType === 'business_screening';
}

function isPostLoginBlocked(pathname: string): boolean {
  if (POST_LOGIN_BLOCKED_PATHS.has(pathname)) {
    return true;
  }
  return POST_LOGIN_BLOCKED_PREFIXES.some((prefix) =>
    pathMatchesPrefix(pathname, prefix)
  );
}

export function getPathPermissionKey(path: string): string | null {
  const pathname = getPathname(path);
  const matchedRule = ROUTE_PERMISSION_RULES.find((rule) =>
    pathMatchesPrefix(pathname, rule.pathPrefix)
  );
  return matchedRule ? matchedRule.permissionKey : null;
}

export function isWorkbenchOnlyUser(
  permissions: PermissionSnapshot | null | undefined
): boolean {
  if (!permissions || permissions.is_admin) {
    return false;
  }

  const normalizedPermissions = getNormalizedMenuPermissions(permissions);
  return (
    normalizedPermissions.length === 1 &&
    normalizedPermissions[0] === 'workbench'
  );
}

export function canAccessPath(
  path: string,
  permissions: PermissionSnapshot | null | undefined
): boolean {
  const { pathname, searchParams } = getPathInfo(path);

  if (ALWAYS_ALLOWED_PATHS.has(pathname)) {
    return true;
  }

  const permissionKey = getPathPermissionKey(pathname);
  if (!permissionKey) {
    return true;
  }

  if (hasMenuPermission(permissions, permissionKey)) {
    return true;
  }

  // 深链场景兜底：详情/审批动作交给页面内及后端按人员身份控制
  if (
    isOfferDetailPath(pathname) ||
    isInterviewDetailPath(pathname) ||
    isBusinessScreeningDeepLink(pathname, searchParams)
  ) {
    return true;
  }

  return false;
}

export function canAccessRuntimePath(
  path: string,
  permissions: PermissionSnapshot | null | undefined
): boolean {
  const pathname = getPathname(path);

  if (ALWAYS_ALLOWED_PATHS.has(pathname)) {
    return true;
  }

  const permissionKey = getPathPermissionKey(pathname);
  if (!permissionKey) {
    return true;
  }

  return hasMenuPermission(permissions, permissionKey);
}

export function getFirstAccessiblePath(
  permissions: PermissionSnapshot | null | undefined
): string {
  const accessiblePath = FIRST_ACCESSIBLE_PATH_ORDER.find((path) =>
    canAccessRuntimePath(path, permissions)
  );
  return accessiblePath || NO_PERMISSION_PATH;
}

export function resolvePostLoginPath(
  preferredPath: string | null | undefined,
  permissions: PermissionSnapshot | null | undefined
): string {
  const parsedPreferredPath = extractInternalPath(preferredPath);
  if (parsedPreferredPath) {
    const preferredPathname = getPathname(parsedPreferredPath);
    if (
      !isPostLoginBlocked(preferredPathname) &&
      canAccessRuntimePath(parsedPreferredPath, permissions)
    ) {
      return parsedPreferredPath;
    }
  }
  return getFirstAccessiblePath(permissions);
}
